/**
 * Register Shopify Integration
 * Registers the Shopify integration with the integration registry
 */

import { IntegrationRegistry } from '../registry/IntegrationRegistry';
import { ShopifyIntegrationFactory } from './ShopifyIntegrationFactory';
import { IntegrationConfig } from '../../../types/integration';

export function registerShopifyIntegration(): void {
  const registry = IntegrationRegistry.getInstance();
  registry.register(
    'shopify',
    'Shopify',
    'ecommerce',
    'shopify',
    '1.0.0',
    {
      create: async (config: IntegrationConfig) => {
        return ShopifyIntegrationFactory.fromConfig(config);
      },
      validate: async (config: Partial<IntegrationConfig>) => {
        const validation = ShopifyIntegrationFactory.validateParams(config as any);
        return {
          valid: validation.valid,
          errors: validation.errors.map(error => ({
            field: 'config',
            message: error,
            code: 'VALIDATION_ERROR'
          })),
          warnings: []
        };
      },
      getConfigSchema: () => ({
        type: 'object',
        required: ['shopDomain'],
        properties: {
          shopDomain: {
            type: 'string',
            title: 'Shop Domain',
            description: 'Your Shopify store domain (without .myshopify.com)',
            pattern: '^[a-zA-Z0-9][a-zA-Z0-9\\-]*[a-zA-Z0-9]$'
          },
          usePrivateApp: {
            type: 'boolean',
            title: 'Use Private App',
            description: 'Use private app credentials instead of OAuth',
            default: false
          },
          accessToken: {
            type: 'string',
            title: 'Access Token',
            description: 'Private app access token (required for private apps)'
          },
          apiKey: {
            type: 'string',
            title: 'API Key',
            description: 'App API key (required for OAuth apps)'
          },
          apiSecret: {
            type: 'string',
            title: 'API Secret',
            description: 'App API secret (required for OAuth apps)'
          },
          webhookSecret: {
            type: 'string',
            title: 'Webhook Secret',
            description: 'Secret for webhook signature verification'
          },
          apiVersion: {
            type: 'string',
            title: 'API Version',
            description: 'Shopify API version to use',
            default: '2023-10',
            pattern: '^\\d{4}-\\d{2}$'
          },
          scopes: {
            type: 'array',
            title: 'API Scopes',
            description: 'Required API scopes for the integration',
            items: {
              type: 'string'
            },
            default: [
              'read_products',
              'write_products',
              'read_customers',
              'write_customers',
              'read_orders',
              'write_orders',
              'read_inventory',
              'write_inventory',
              'read_checkouts',
              'write_checkouts'
            ]
          }
        }
      }),
      getSupportedFeatures: () => [
        'product_sync',
        'customer_sync',
        'order_sync',
        'cart_abandonment',
        'inventory_tracking',
        'product_recommendations',
        'webhooks',
        'revenue_attribution'
      ]
    }
  );
}